import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import SurroundSoundRoundedIcon from "@mui/icons-material/SurroundSoundRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function StereoImagingLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Stereo Imaging in Music Production: What It Is and How It Works",
      description:
        "Learn what stereo imaging is, how width and placement affect a mix, what mono compatibility means, and how stereo imaging is used in music production and mastering.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/stereo-imaging",
      articleSection: "Audio Education",
      keywords: [
        "stereo imaging",
        "what is stereo imaging",
        "stereo width",
        "mono compatibility",
        "mid side processing",
        "stereo field",
        "mix width",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Stereo imaging shapes how wide, narrow, centered, or spacious a sound or full mix feels across the left and right speakers.",
      icon: <SurroundSoundRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Good stereo imaging helps a mix feel bigger, clearer, and more immersive while giving each element a more intentional place.",
      icon: <OpenInFullRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Mixing, mastering, stereo enhancement, panning decisions, ambience control, bus processing, and sound design.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const imagingTypes = [
    {
      name: "Panning-Based Imaging",
      description:
        "Creates width and separation by placing sounds at different points between the left and right channels.",
    },
    {
      name: "Mid/Side Imaging",
      description:
        "Separates the center information from the side information, allowing width changes without affecting both the same way.",
    },
    {
      name: "Delay-Based Widening",
      description:
        "Uses tiny timing differences between left and right to create a wider stereo impression, sometimes inspired by the Haas effect.",
    },
    {
      name: "Modulation-Based Width",
      description:
        "Uses chorus, flanger, phaser, or micro-pitch style effects to add motion and make a source feel wider.",
    },
    {
      name: "Ambience-Based Imaging",
      description:
        "Uses reverb or room information to push a sound outward and create a larger sense of space around it.",
    },
    {
      name: "Mastering Width Control",
      description:
        "Applies careful stereo shaping to the full mix, often with subtle adjustments to preserve focus and mono compatibility.",
    },
  ];

  const imagingConcepts = [
    {
      name: "Center vs Sides",
      description:
        "The center contains information shared equally by the left and right channels, while the sides contain the differences that create width.",
    },
    {
      name: "Stereo Width",
      description:
        "Width describes how far a sound appears to spread across the stereo field, from narrow and centered to wide and spacious.",
    },
    {
      name: "Depth and Space",
      description:
        "Stereo imaging is not only about left and right. It also influences how open, deep, and three-dimensional a mix feels.",
    },
    {
      name: "Mono Compatibility",
      description:
        "A very wide sound can become weak or strange when collapsed to mono if the left and right channels differ too much.",
    },
  ];

  const controls = [
    {
      name: "Width",
      description:
        "Adjusts how wide the stereo image feels. Higher settings often increase side information relative to the center.",
    },
    {
      name: "Pan",
      description:
        "Moves an element left or right in the stereo field, which is one of the most basic and powerful imaging tools.",
    },
    {
      name: "Mid / Side Balance",
      description:
        "Controls the relationship between center content and side content, allowing more precise image shaping.",
    },
    {
      name: "Delay / Offset",
      description:
        "Some stereo wideners use tiny timing offsets between left and right to create more apparent width.",
    },
    {
      name: "Frequency-Dependent Width",
      description:
        "Lets you widen only certain parts of the spectrum, which is useful because low frequencies often need to stay more centered.",
    },
    {
      name: "Stereo Link / Correlation Monitoring",
      description:
        "Helps track whether your width settings remain stable and mono-safe rather than becoming phasey or hollow.",
    },
  ];

  const commonUses = [
    "Placing instruments so they do not all fight for the center",
    "Making pads, backing vocals, and effects feel larger and more immersive",
    "Keeping kick, snare, bass, and lead vocal focused in the middle",
    "Adding width to guitars, synths, and ambience without overwhelming the mix",
    "Refining the sense of space in a mastering chain",
    "Creating contrast between narrow verses and wider choruses",
  ];

  const bestPractices = [
    "Treat width as a musical decision, not just a way to make everything sound bigger.",
    "Keep important low-end elements mostly centered unless you have a clear reason not to.",
    "Check your mix in mono regularly, especially when using wideners or heavy stereo effects.",
    "Use contrast. A mix often feels wider when some things stay narrow and focused.",
    "Be careful with extreme widening on the full mix, because it can weaken punch and create phase issues.",
  ];

  const relatedLinks = [
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Learn how tonal balance affects clarity and separation, which strongly influences perceived width and space.",
    },
    {
      title: "Reverb",
      to: "/learning/reverb",
      description:
        "See how reverb adds depth and space, which often works hand in hand with stereo imaging.",
    },
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "Explore how chorus can widen and thicken sounds using modulation and subtle stereo movement.",
    },
    {
      title: "Phaser",
      to: "/learning/phaser",
      description:
        "Understand how phasers add movement and motion, which can interact with stereo placement and width.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Stereo Imaging | Parish Audio Learning"
        description="Learn what stereo imaging is, how width and placement affect a mix, what mono compatibility means, and how stereo imaging is used in music production and mastering."
        canonical="https://parishaudio.com/learning/stereo-imaging"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Stereo Imaging in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                Stereo imaging is the art and science of shaping how sound is distributed
                across the left and right speakers. It affects whether a sound feels narrow,
                centered, wide, spacious, or immersive, and it plays a huge role in how open
                and professional a mix feels.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                Good stereo imaging helps create separation, depth, and contrast. It can make
                a chorus feel huge, a vocal feel focused, a guitar feel expansive, or a full
                mix feel balanced and intentional. Done poorly, it can make a mix feel blurry,
                phasey, or weak when played back in mono.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.97rem" }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                What is stereo imaging?
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                Stereo imaging refers to how audio is positioned and perceived between the left
                and right channels of a stereo playback system. It influences where sounds seem
                to sit, how wide they feel, and how much dimension a mix appears to have.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                Some of stereo imaging comes from simple panning. Some comes from natural
                differences between microphones, room reflections, reverb, timing offsets,
                modulation, or dedicated stereo processing tools. Together, these factors shape
                the sense of size, spread, and realism in a mix.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                Stereo imaging is not just about making things wide. It is about making the
                stereo field feel intentional. A focused center can make a lead vocal feel
                strong. Carefully placed sides can make a mix feel large without losing clarity.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How stereo imaging works
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {imagingConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                When the left and right channels are identical, the sound feels centered.
                As the differences between them increase, the image can feel wider. Those
                differences might come from level, timing, frequency balance, ambience, or
                phase relationships. Stereo imaging tools shape those differences to control
                how the listener perceives space.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common stereo imaging approaches
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {imagingTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Stereo imaging vs panning
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Related, but not identical
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Panning is one of the main building blocks of stereo imaging, but stereo
                    imaging is a broader concept. Panning decides where a sound sits from left
                    to right. Stereo imaging also includes width, ambience, side content, and
                    the overall spatial relationships between elements.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In other words, panning places sounds. Stereo imaging shapes the whole
                    perceived space around them. A sound can be centered but still feel wide,
                    or panned but still feel narrow, depending on how it is processed.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common stereo imaging controls
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How stereo imaging is used in mixing and mastering
              </Typography>

              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                In a mix, stereo imaging helps prevent everything from stacking on top of the
                lead vocal and snare in the center. In mastering, it is often used more subtly
                to refine width, preserve focus, and create a balanced sense of openness without
                damaging mono playback or making the mix feel unstable.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common stereo imaging mistakes
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  {[
                    "Making everything wide, which removes contrast and can actually make the mix feel less impressive.",
                    "Widening low frequencies too much, which can weaken the center and reduce playback stability.",
                    "Ignoring mono compatibility and ending up with phase problems or disappearing elements.",
                    "Using wideners to fix arrangement problems that would be better solved with panning, EQ, or level balance.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Best practices for stereo imaging
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Final thoughts
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                Stereo imaging is one of the biggest contributors to how spacious and
                professional a mix feels. It helps create size, contrast, and separation,
                but it works best when used with intention rather than pushed everywhere.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                A great stereo image is not only wide. It is balanced, focused, and musical.
                When center and sides work together well, the mix feels open without losing
                strength, clarity, or mono compatibility.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}